import React from "react";

import { Picto } from "@amphore-dev/amphore-lib";
import { t } from "i18next";

import { Dot } from "@/components/atoms/Dot/Dot";

import { cn } from "@/utils/cn";

export interface IEntityRowProps {
	color: string;
	name: React.ReactNode;
	description?: React.ReactNode;
	/** Figures on the right of the name, each in its own cell. */
	metrics?: React.ReactNode[];
	isArchived?: boolean;
	onClick?: () => void;
	className?: string;
	"data-testid"?: string;
}

/**
 * A list row for a coloured entity (a project a view gathers): dot,
 * name and description, figures, an archive mark when archived, a chevron when it
 * opens something.
 */
export const EntityRow: React.FC<IEntityRowProps> = ({
	color,
	name,
	description,
	metrics = [],
	isArchived = false,
	onClick,
	className,
	...rest
}) => {
	const Tag = onClick ? "button" : "div";

	return (
		<Tag
			type={onClick ? "button" : undefined}
			onClick={onClick}
			className={cn([
				"flex w-full items-center gap-3 px-5 py-3 text-left",
				onClick && "cursor-pointer hover:bg-neutral-50",
				isArchived && "text-neutral-500",
				className,
			])}
			data-testid={rest["data-testid"]}
		>
			<Dot color={color} className="h-2.5 w-2.5 min-w-2.5" />
			<span className="flex min-w-0 min-w-50 grow flex-col">
				<span className="flex items-center gap-1.5 truncate text-sm font-medium">
					{name}
					{isArchived && (
						<span title={t("projects.infos.archived")}>
							<Picto
								icon="archive"
								className="h-3 w-3 text-neutral-400"
							/>
						</span>
					)}
				</span>
				{description && (
					<span className="truncate text-xs text-neutral-500">
						{description}
					</span>
				)}
			</span>
			{metrics.map((metric, index) => (
				<span
					key={index}
					className="hidden shrink-0 text-xs text-neutral-600 sm:block sm:min-w-20 sm:text-right"
				>
					{metric}
				</span>
			))}
			{onClick && (
				<Picto icon="chevron" className="h-4 w-4 text-neutral-400" />
			)}
		</Tag>
	);
};
